import { useMutation, useQuery, useQueryCache } from "@pinia/colada";
import {
	CreatePublicPage,
	PublicPage,
	UpdatePublicPage,
	useApi,
	UserScope,
} from "@soft-db/monitoring-api-client";
import { composeCacheEffects, useQueryEnabled, useScopedQuery } from "./query";
import { sitesKeys } from "./site-query";

// --- keys ---
export const publicPagesKeys = {
	root: ["publicPages"] as const,

	list: () => [...publicPagesKeys.root, "list"] as const,

	bySpace: (space: string) => [...publicPagesKeys.list(), "space", space] as const,
	byDist: (dist: string) => [...publicPagesKeys.list(), "dist", dist] as const,

	byId: (id: string) => [...publicPagesKeys.root, "id", id] as const,
};

// --- Cache effects ---

function useUpdateCacheById() {
	const cache = useQueryCache();

	return (page: PublicPage) => {
		cache.setQueryData(publicPagesKeys.byId(page.id), page);
	};
}

function useInvalidateListCache() {
	const cache = useQueryCache();

	return () => {
		cache.invalidateQueries({ key: publicPagesKeys.list() });
	};
}

/**
 * Compose both updateCacheById and invalidateListCache effects
 */
function usePublicPageCacheEffect() {
	return composeCacheEffects(useUpdateCacheById(), useInvalidateListCache());
}

// --- Queries ---

export function usePublicPage(id: MaybeRefOrGetter<string>) {
	const api = useApi().publicPage();

	const { data: publicPage, ...rest } = useScopedQuery({
		key: () => publicPagesKeys.byId(toValue(id)),
		query: () => api.fetch(toValue(id)),
		enabled: useQueryEnabled(id),
	});

	return {
		publicPage,
		...rest,
	};
}

export function useSpacePublicPages(space: MaybeRefOrGetter<string>) {
	const api = useApi().publicPage();

	const { data: publicPages, ...rest } = useQuery({
		key: () => publicPagesKeys.bySpace(toValue(space)),
		query: () => api.fetchSpacePages(toValue(space)),
		enabled: useQueryEnabled(space),
	});

	return { publicPages, ...rest };
}

export function useDistPublicPages(space: MaybeRefOrGetter<string>) {
	const api = useApi().publicPage();

	const { data: publicPages, ...rest } = useQuery({
		key: () => publicPagesKeys.byDist(toValue(space)),
		query: () => api.fetchDistPages(toValue(space)),
		enabled: useQueryEnabled(space),
	});

	return { publicPages, ...rest };
}

// --- Mutations ---

export function useCreatePublicPage() {
	const api = useApi().publicPage();

	return useMutation({
		mutation: ({ scope, page }: { scope: UserScope; page: CreatePublicPage }) => {
			switch (scope.type) {
				case "distributor":
					return api.createInDist(scope.id, page);
				case "space":
					return api.createInSpace(scope.id, page);
				case "none":
					throw new Error("invalid scope for public page creation");
			}
		},
		onSuccess: useInvalidateListCache(),
	});
}

export function useUpdatePublicPage() {
	const api = useApi().publicPage();

	return useMutation({
		mutation: ({ id, data }: { id: string; data: UpdatePublicPage }) => api.update(id, data),
		onSuccess: usePublicPageCacheEffect(),
	});
}

export function useUpdatePublicPageAvatar() {
	const api = useApi().publicPage();

	return useMutation({
		mutation: ({ id, image }: { id: string; image: File }) => api.updateAvatar(id, image),
		onSuccess: usePublicPageCacheEffect(),
	});
}

export function useDeletePublicPageAvatar() {
	const api = useApi().publicPage();

	return useMutation({
		mutation: ({ id }: { id: string }) => api.deleteAvatar(id),
		onSuccess: usePublicPageCacheEffect(),
	});
}

export function useAddPublicPageSite() {
	const api = useApi().publicPage();

	const cache = useQueryCache();

	return useMutation({
		mutation: ({ id, site }: { id: string; site: string }) => api.addSite(id, site),
		onSuccess(_, { id }) {
			cache.invalidateQueries({ key: sitesKeys.byPublicPage(id) });
		},
	});
}

export function useRemovePublicPageSite() {
	const api = useApi().publicPage();

	const cache = useQueryCache();

	return useMutation({
		mutation: ({ id, site }: { id: string; site: string }) => api.removeSite(id, site),
		onSuccess(_, { id }) {
			cache.invalidateQueries({ key: sitesKeys.byPublicPage(id) });
		},
	});
}

export function useDeletePublicPage() {
	const api = useApi().publicPage();

	return useMutation({
		mutation: (id: string) => api.delete(id),
		onSuccess: useInvalidateListCache(),
	});
}
